import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { siteInfo, categories } from "@/mocks/manga";

const milestones = [
  { year: "2016", title: "漫画网诞生", desc: "初心是让每个漫画爱好者都能流畅、舒适地看漫画。" },
  { year: "2018", title: "阅读器全面升级", desc: "上线漫画翻页模式与横屏阅读，阅读体验大幅提升。" },
  { year: "2020", title: "夜间模式上线", desc: "推出护眼的漫画夜间模式，深夜追漫更舒适。" },
  { year: "2022", title: "缓存下载支持", desc: "支持漫画缓存下载与阅读设置，离线也能随时看漫画。" },
  { year: "2024", title: "服务千万读者", desc: "累计服务超千万漫画读者，成为深受欢迎的漫画阅读平台。" },
];

const values = [
  { icon: "ri-leaf-line", title: "阅读体验", desc: "专注打磨漫画阅读器，翻页、横屏、夜间模式样样顺手。" },
  { icon: "ri-heart-line", title: "热爱漫画", desc: "团队都是资深漫画迷，懂你想要的每一种阅读感受。" },
  { icon: "ri-shield-check-line", title: "尊重版权", desc: "坚持正版内容与创作者权益，打造健康的漫画生态。" },
  { icon: "ri-community-line", title: "社区氛围", desc: "构建友善的漫画社区，让同好一起分享与讨论。" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于漫画网",
            url: "https://mangains.github.io/about",
            description: `${siteInfo.name}是专业的漫画阅读平台，提供漫画翻页模式、横屏阅读、夜间模式、缓存下载与阅读设置。`,
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", name: "漫画网", url: "https://mangains.github.io/" },
            mainEntity: {
              "@type": "Organization",
              name: siteInfo.name,
              foundingDate: "2016",
              email: siteInfo.email,
              telephone: siteInfo.phone,
              address: { "@type": "PostalAddress", streetAddress: siteInfo.address, addressCountry: "CN" },
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://mangains.github.io/" },
              { "@type": "ListItem", position: 2, name: "关于我们", item: "https://mangains.github.io/about" },
            ],
          },
        ]}
      />
      <Navbar />

      <main className="pt-24 md:pt-28 pb-16">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20cozy%20illustration%20of%20people%20reading%20comics%20and%20manga%20together%20in%20a%20stylish%20modern%20space%2C%20warm%20orange%20and%20gold%20palette%2C%20friendly%20community%20atmosphere%2C%20storytelling%20vibe%2C%20colorful%20artwork&width=1600&height=700&seq=about-hero-2026&orientation=landscape"
              alt="关于漫画网"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-background-950/70"></div>
          </div>
          <div className="relative container-site w-full py-20 md:py-28 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/15 backdrop-blur text-background-50 text-sm border border-background-50/25 mb-5">
              <i className="ri-flag-2-fill text-accent-500"></i>
              关于我们
            </span>
            <h1 className="font-heading text-4xl md:text-5xl text-background-50">让看漫画更顺手</h1>
            <p className="mt-4 text-background-100 max-w-2xl mx-auto">
              {siteInfo.name}成立于 {siteInfo.founded} 年，始终专注于打造专业、舒适的漫画阅读体验。
            </p>
          </div>
        </section>

        {/* 品牌介绍 */}
        <section className="container-site py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">我们的品牌故事</h2>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                {siteInfo.name}创立于 {siteInfo.founded} 年，源于一群热爱漫画的创作者的共同理想——让每一个漫画爱好者都能拥有顺畅、舒适的阅读体验。
              </p>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                从最初简单的翻页阅读，到如今支持漫画翻页模式、漫画横屏阅读、漫画夜间模式、漫画缓存下载与漫画阅读设置的一站式阅读器，
                我们始终把「让看漫画更顺手」作为最高准则，不断打磨每一处细节。
              </p>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                如今，{siteInfo.name}已成为深受漫画爱好者喜爱的阅读平台，汇聚热血、恋爱、悬疑、科幻、搞笑、冒险、奇幻、校园等丰富题材，持续为读者带来优质内容。
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-start gap-3">
                <Link to="/category" className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
                  开始浏览漫画
                </Link>
                <Link to="/" className="px-7 py-3 rounded-full bg-background-100 border border-background-300 text-foreground-950 font-semibold hover:bg-background-200 transition-colors whitespace-nowrap">
                  返回首页
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=stylish%20illustration%20of%20a%20team%20of%20creators%20designing%20a%20manga%20reader%20app%20at%20a%20desk%20with%20drawings%2C%20warm%20orange%20lighting%2C%20creative%20workspace%2C%20artistic%20digital%20art&width=900&height=700&seq=about-story-2026&orientation=landscape"
                alt="漫画网品牌故事"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* 数据统计 */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="container-site grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { num: "1000+", label: "精品漫画" },
              { num: "5000万+", label: "累计读者" },
              { num: "10亿+", label: "在线阅读量" },
              { num: "99%", label: "读者满意度" },
            ].map((s) => (
              <div key={s.label}>
                <p className="font-heading text-3xl md:text-4xl text-primary-600">{s.num}</p>
                <p className="mt-2 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 发展历程 */}
        <section className="container-site py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">发展历程</h2>
            <p className="mt-1 text-sm text-foreground-600">一路相伴，持续成长</p>
          </div>
          <div className="relative max-w-3xl mx-auto pl-8 md:pl-0">
            <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
            <div className="space-y-8">
              {milestones.map((m, idx) => (
                <div key={m.year} className={`relative flex md:w-1/2 ${idx % 2 === 0 ? "md:pr-10 md:text-right" : "md:ml-auto md:pl-10"}`}>
                  <span className={`absolute left-3 md:left-auto top-1.5 -translate-x-1/2 md:translate-x-0 w-3.5 h-3.5 rounded-full bg-accent-500 ring-4 ring-background-50 ${idx % 2 === 0 ? "md:-right-1.5" : "md:-left-1.5"}`}></span>
                  <div className="ml-6 md:ml-0">
                    <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">{m.year}</span>
                    <h3 className="mt-2 font-semibold text-foreground-950">{m.title}</h3>
                    <p className="mt-1 text-sm text-foreground-600">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 价值观 */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="container-site">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">我们坚持的价值观</h2>
              <p className="mt-1 text-sm text-foreground-600">指引我们前行的信念</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {values.map((v) => (
                <div key={v.title} className="rounded-xl border border-background-200 bg-background-50 p-6 text-center">
                  <span className="w-12 h-12 mx-auto rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-semibold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 口碑 */}
        <section className="container-site py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">读者口碑</h2>
            <p className="mt-1 text-sm text-foreground-600">来自真实读者的声音</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              { name: "阿布", role: "资深漫画迷", text: "夜间模式太舒服了，深夜看漫画再也不刺眼，翻页也很流畅，强烈推荐！", stars: 5 },
              { name: "小鹿", role: "悬疑漫画控", text: "横屏阅读双页模式看战斗分镜特别爽，缓存下载功能让我在地铁上也能看漫画。" , stars: 5 },
              { name: "老王", role: "热血漫爱好者", text: "阅读设置很贴心，可以自定义翻页方式，这个阅读器是同类里最好用的。" , stars: 5 },
            ].map((t) => (
              <figure key={t.name} className="rounded-xl border border-background-200 bg-background-100 p-6">
                <div className="flex items-center gap-1 text-accent-500 mb-3">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <blockquote className="text-sm text-foreground-700 leading-relaxed">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center text-background-50 font-medium">{t.name[0]}</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-600">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* 联系我们 */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="container-site grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">联系我们</h2>
              <p className="mt-3 text-foreground-700 leading-relaxed">
                无论是合作、建议还是反馈，我们都很乐意倾听你的声音。欢迎随时联系我们，一起让看漫画更顺手。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">邮箱</p>
                    <a href={`mailto:${siteInfo.email}`} className="text-foreground-950 font-medium hover:text-primary-600 transition-colors">{siteInfo.email}</a>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">电话</p>
                    <p className="text-foreground-950 font-medium">{siteInfo.phone}</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">地址</p>
                    <p className="text-foreground-950 font-medium">{siteInfo.address}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950 mb-5">我们的分类</h3>
              <div className="grid grid-cols-2 gap-3">
                {categories.filter((c) => c !== "全部").map((cat) => (
                  <Link
                    key={cat}
                    to={`/category?type=${encodeURIComponent(cat)}`}
                    className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-950 hover:border-primary-300 hover:bg-background-200 transition-colors"
                  >
                    <i className="ri-book-2-line text-primary-500"></i>
                    {cat}漫画
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container-site py-14 md:py-20">
          <div className="rounded-2xl bg-background-950 relative overflow-hidden px-6 py-12 md:py-16 text-center">
            <div className="absolute inset-0 opacity-20">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20colorful%20manga%20comic%20background%20with%20halftone%20dots%20and%20dynamic%20lines%2C%20warm%20orange%20gold%20and%20crimson%20palette&width=1200&height=400&seq=about-cta-2026&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative w-full">
              <h2 className="font-heading text-3xl md:text-4xl text-background-50">现在就开启你的漫画之旅</h2>
              <p className="mt-3 text-background-100 max-w-xl mx-auto">海量漫画，翻页、横屏、夜间模式样样顺手，等你来探索。</p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/category" className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
                  开始阅读漫画
                </Link>
                <Link to="/" className="px-7 py-3 rounded-full bg-background-50/15 backdrop-blur text-background-50 font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap">
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}